/*
 * Copyright (c)    LLC 2025.
 * Proprietary License.
 * Redistribution and use in source and binary forms, with or without
 * modification, are restricted.
 */

import React, { useState } from 'react';
import { XCircleIcon } from 'lucide-react';
import { useSettings } from '@/context/settings-context/settings-context.ts';
import { useScreenShareSettings } from '@/context/call-context/use-screen-share-settings.ts';
import { UserPlan } from '@/models';
import { useAuth } from '@/context/auth-context/use-auth.ts';
import { API_BASE } from '@/config.ts';
import { useTranslation } from 'react-i18next';
import { HorizontalSelector } from '@/components/horizontal-selector.tsx';
import { cn } from '@/lib/utils.ts';
import { isPardusBrowser } from '@/components/check-user-agent.tsx';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PiSpeakerHigh } from 'react-icons/pi';

// Electron has no type property yet
interface ElectronSource {
  id: string;
  name: string;
  thumbnail: string | null; // Data URL
  appIcon: string | null; // Data URL
  displayId: string | null;
}

interface SourceItemProps {
  source: ElectronSource;
  selected?: boolean;
  onSelect: (source: ElectronSource) => void;
}

type ResolutionPreset = '360p' | '720p' | '1080p' | '4k';
type FpsPreset = 3 | 5 | 15 | 30 | 60;

type SelectedSource = {
  id: string;
  name: string;
  thumbnail: string | null;
  stream?: MediaStream;
  resolution?: { width: number; height: number };
  fps?: number;
};

type SourceSelectorModalProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (source: SelectedSource) => void;
  types?: string[]; // e.g. ['screen', 'window']
};

/**
 * Screen sharing selector
 * @experimental
 * @param open
 * @param onClose
 * @param onSelect
 * @param types
 * @constructor
 */
export const SourceSelectorModal: React.FC<SourceSelectorModalProps> = ({
  open,
  onClose,
  onSelect,
  types = ['screen', 'window'],
}) => {
  const [sources, setSources] = React.useState<Array<any>>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { captureSystemAudio, setCaptureSystemAudio } = useSettings();
  const { setSelectedSharePreset } = useScreenShareSettings();
  const [plan, setPlan] = useState<UserPlan | null>(UserPlan.FREE);
  const { user: sessionUser } = useAuth();
  const { t } = useTranslation();
  const [resolution, setResolution] = useState<ResolutionPreset>('720p');
  const [fps, setFps] = useState<FpsPreset>(30);
  const [selectedSource, setSelectedSource] = useState<ElectronSource | null>(null);
  const [starting, setStarting] = useState(false);

  // Default selects screen (FAST)
  // const [activeTab, setActiveTab] = useState<'window' | 'screen'>(() => {
  //   if (types.includes('screen') && types.includes('window')) {
  //     return 'screen';
  //   }
  //   return types.includes('window') ? 'window' : 'screen';
  // });

  // Состояние для активного таба. По умолчанию выбирается первый доступный из types. медленно
  const [activeTab, setActiveTab] = useState<'window' | 'screen'>(
    types.includes('window') ? 'window' : 'screen'
  );

  // В Electron ID источников начинаются с "window:" или "screen:"
  const isWindowSource = (source: ElectronSource) => source.id.startsWith('window:');
  const isScreenSource = (source: ElectronSource) => source.id.startsWith('screen:');

  const filteredSources = React.useMemo(() => {
    if (!window.electronAPI?.getSources) return sources;

    return sources.filter((s) => {
      if (activeTab === 'window') return isWindowSource(s);
      if (activeTab === 'screen') return isScreenSource(s);
      return true;
    });
  }, [sources, activeTab]);

  const RESOLUTION_MAP: Record<
    ResolutionPreset,
    {
      width: number;
      height: number;
    }
  > = {
    '360p': {
      width: 640,
      height: 360,
    },
    '720p': {
      width: 1280,
      height: 720,
    },
    '1080p': {
      width: 1920,
      height: 1080,
    },
    '4k': {
      width: 3840,
      height: 2160,
    },
  };

  const handleAudioToggle = (checked: boolean) => setCaptureSystemAudio(checked);

  function getMaxResolutionForPlan(plan: string) {
    switch (plan) {
      case 'EXTENDED':
        return {
          width: 3840,
          height: 2160,
        };
      case 'OPTIMIM':
        return {
          width: 1920,
          height: 1080,
        };
      case 'BASIC':
        return {
          width: 1920,
          height: 1080,
        };
      case 'FREE':
      default:
        return {
          width: 1280,
          height: 720,
        };
    }
  }

  const isResolutionAllowed = (preset: ResolutionPreset, plan: UserPlan) => {
    if (preset === '360p' || preset === '720p') return true;
    if (preset === '1080p') return plan !== UserPlan.FREE;
    if (preset === '4k') return plan === UserPlan.EXTENDED;
    return false;
  };

  const isFpsAllowed = (fps: FpsPreset, plan: UserPlan) => {
    if (fps <= 30) return true;
    return plan !== UserPlan.FREE;
  };

  const handleSelectSource = (source: ElectronSource) => {
    setSelectedSource(source);
    setError(null);
  };

  const handleStartSharing = async () => {
    if (!selectedSource) return;

    setStarting(true);

    try {
      const userPlan = plan ?? 'FREE';
      const maxRes = getMaxResolutionForPlan(userPlan);
      const requested = RESOLUTION_MAP[resolution];

      const finalResolution = {
        width: Math.min(requested.width, maxRes.width),
        height: Math.min(requested.height, maxRes.height),
      };

      const videoConstraints: MediaTrackConstraints = {
        frameRate: {
          ideal: fps,
          max: fps,
        },
        width: finalResolution.width,
        height: finalResolution.height,
      };

      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: false,
      };

      let stream: MediaStream;

      if (window.electronAPI?.selectSource) {
        await window.electronAPI.selectSource(selectedSource.id);
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: videoConstraints,
          audio: captureSystemAudio ? audioConstraints : false,
        });
      } else {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: videoConstraints,
          audio: captureSystemAudio ? audioConstraints : false,
        });
      }

      onSelect({
        id: selectedSource.id,
        name: selectedSource.name,
        thumbnail: selectedSource.thumbnail,
        stream,
        resolution: finalResolution,
        fps,
      });

      onClose();
    } catch (err: any) {
      setError(err.message ?? 'Failed to start screen sharing');
    } finally {
      setStarting(false);
    }
  };

  /**
   * Electron has no type, only browser has
   * @param source
   */
  async function handleSelectTypeAndPlanBased(source: any) {
    try {
      // Учитываем префиксы в ID, так как в Electron нет свойства type
      const isWindow = source.type === 'window' || source.id?.startsWith('window:');
      const isScreen = source.type === 'screen' || source.id?.startsWith('screen:');
      const userPlan = plan ?? 'FREE';

      const maxRes = getMaxResolutionForPlan(userPlan);
      const requested = RESOLUTION_MAP[resolution];

      const finalResolution = {
        width: Math.min(requested.width, maxRes.width),
        height: Math.min(requested.height, maxRes.height),
      };

      const videoConstraints: MediaTrackConstraints = {
        frameRate: {
          ideal: fps,
          max: fps,
        },
        width: finalResolution.width,
        height: finalResolution.height,
      };

      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: false,
      };

      let stream: MediaStream | null = null;

      if (window.electronAPI?.selectSource) {
        await window.electronAPI.selectSource(source.id);
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: videoConstraints,
          audio: captureSystemAudio ? audioConstraints : false,
        });
      } else {
        // Browser
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            displaySurface: isScreen ? 'monitor' : 'window',
            ...videoConstraints,
          },
          audio: captureSystemAudio ? audioConstraints : false,
        });
      }

      if (!stream) throw new Error('Не удалось захватить экран');

      onSelect({
        id: source.id,
        name: source.name,
        thumbnail: source.thumbnail,
        stream,
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      onClose();
    }
  }

  /**
   * Initial load from Electron
   */
  React.useEffect(() => {
    if (!open) return;
    let mounted = true;

    async function loadSources() {
      setLoading(true);
      setError(null);
      try {
        let items;
        if (window.electronAPI?.getSources) {
          // @ts-ignore
          items = await window.electronAPI.getSources(types);
          if (!mounted) return;
          setSources(items);
        } else {
          // BROWSER: отображаем единственный вариант — "Browser screen"
          setSources([
            {
              id: 'browser-screen',
              name: 'Выбрать экран',
              thumbnail: null,
              appIcon: null,
              displayId: null,
            },
          ]);
        }
      } catch (err: any) {
        console.error(err);
        setError(err?.message ?? String(err));
      } finally {
        setLoading(false);
      }
    }

    loadSources();
    return () => {
      mounted = false;
    };
  }, [open]);

  // 2. АВТОМАТИЧЕСКОЕ ПЕРЕКЛЮЧЕНИЕ: Если на текущем табе пусто, но есть источники на другом
  React.useEffect(() => {
    if (sources.length > 0 && window.electronAPI?.getSources) {
      const hasWindows = sources.some((s) => isWindowSource(s));
      const hasScreens = sources.some((s) => isScreenSource(s));

      if (activeTab === 'window' && !hasWindows && hasScreens) {
        setActiveTab('screen');
      } else if (activeTab === 'screen' && !hasScreens && hasWindows) {
        setActiveTab('window');
      }
    }
  }, [sources, activeTab]);

  /**
   * Update thumbnails each 3 seconds (Cause grid re-render)
   */
  React.useEffect(() => {
    if (!open || !window.electronAPI?.getSources) return;
    let mounted = true;
    let intervalId: NodeJS.Timeout;

    async function updateSources() {
      try {
        // @ts-ignore
        const items = await window.electronAPI?.getSources(types);
        if (!mounted) return;

        setSources((prevSources) => {
          const prevMap = new Map(prevSources.map((s) => [s.id, s]));
          return items.map((s: any) => ({
            ...s,
            thumbnail: s.thumbnail ?? prevMap.get(s.id)?.thumbnail ?? null,
          }));
        });
      } catch (err) {
        console.error(err);
      }
    }

    updateSources();
    intervalId = setInterval(updateSources, 3_000);

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, [open]);

  React.useEffect(() => {
    if (!sessionUser) {
      return;
    }

    fetch(`${API_BASE}/api/users/${sessionUser.id}`)
      .then((res) => res.json())
      .then((data) => {
        setPlan(data.plan);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [sessionUser]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white dark:bg-zinc-900 rounded-lg shadow-xl w-full max-w-xl p-5">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-zinc-800 dark:text-zinc-100">
            {t('source_selector_modal.share_screen_or_window')}
          </h3>
          <button onClick={onClose} className="text-sm">
            <XCircleIcon
              size={26}
              className="text-neutral-500 hover:text-neutral-800 dark:hover:text-white transition-colors text-xl leading-none"
            />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-x-2 gap-y-1 mb-3 items-start">
          <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Resolution</p>
          <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">FPS</p>
          <HorizontalSelector
            value={resolution}
            onChange={setResolution}
            options={[
              {
                value: '360p',
                label: '360p',
              },
              {
                value: '720p',
                label: '720p',
              },
              {
                value: '1080p',
                label: '1080p',
                locked: !isResolutionAllowed('1080p', plan!),
              },
              {
                value: '4k',
                label: '4K',
                locked: !isResolutionAllowed('4k', plan!),
              },
            ]}
          />
          <HorizontalSelector
            value={fps}
            onChange={setFps}
            options={[
              {
                value: 5,
                label: '5',
              },
              {
                value: 15,
                label: '15',
              },
              {
                value: 30,
                label: '30',
              },
              {
                value: 60,
                label: '60',
                locked: !isFpsAllowed(60, plan!),
              },
            ]}
          />
        </div>

        {/* ТАБЫ: Отображаются только если это Electron и запрошены оба типа */}
        {/*@ts-ignore*/}
        {window.electronAPI?.getSources && types.includes('window') && types.includes('screen') && (
          <div className="flex mb-4 border-b border-zinc-200 dark:border-zinc-700">
            <button
              onClick={() => setActiveTab('window')}
              className={cn(
                'px-4 py-2 text-sm font-medium transition-colors relative',
                activeTab === 'window'
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200'
              )}
            >
              {t('source_selector_modal.windows', 'Окна')}
              {activeTab === 'window' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('screen')}
              className={cn(
                'px-4 py-2 text-sm font-medium transition-colors relative',
                activeTab === 'screen'
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200'
              )}
            >
              {t('source_selector_modal.screens', 'Экраны')}
              {activeTab === 'screen' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400" />
              )}
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto">
          {loading && (
            <div className="col-span-2 text-center text-zinc-500 py-4">
              {' '}
              {t('source_selector_modal.loading')}…
            </div>
          )}
          {error && <div className="col-span-2 text-red-500 mb-3 text-center">{error}</div>}

          {!loading && filteredSources.length === 0 && (
            <div className="col-span-2 text-center text-zinc-500 py-4">
              {activeTab === 'window'
                ? t('source_selector_modal.no_windows', 'Нет доступных окон')
                : t('source_selector_modal.no_screens', 'Нет доступных экранов')}
            </div>
          )}

          {!loading &&
            filteredSources.map((s) => {
              if (isPardusBrowser()) {
                return (
                  <SourceItem
                    key={s.id}
                    source={s}
                    selected={selectedSource?.id === s.id}
                    onSelect={handleSelectSource}
                  />
                );
              } else {
                return <SourceItem key={s.id} source={s} onSelect={handleSelectTypeAndPlanBased} />;
              }
            })}
        </div>

        {/*Move to bottom right before capture/cancel*/}
        <div className="my-4 flex justify-end items-center gap-2">
          <PiSpeakerHigh className="text-muted-foreground" />
          <label
            htmlFor="audio"
            className="text-sm font-medium text-gray-800 dark:text-gray-100 cursor-pointer select-none hover:text-indigo-600 transition-colors"
          >
            {t('source_selector_modal.capture_sound')}
          </label>
          <input
            id="audio"
            type="checkbox"
            checked={captureSystemAudio}
            onChange={(e) => handleAudioToggle(e.target.checked)}
            className="h-4 w-4 border border-gray-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all cursor-pointer"
          />
        </div>

        {isPardusBrowser() && (
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm rounded-md border border-zinc-300 dark:border-zinc-700"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleStartSharing}
              disabled={!selectedSource || starting}
              className={cn(
                'px-4 py-1.5 text-sm rounded-md font-medium transition',
                !selectedSource || starting
                  ? 'bg-zinc-300 dark:bg-zinc-700 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              )}
            >
              {starting ? t('common.starting') : t('source_selector_modal.start_sharing')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const SourceItem = React.memo(({ source, onSelect, selected }: SourceItemProps) => (
  <div
    className={cn(
      'border rounded-lg p-2 cursor-pointer transition',
      selected ? 'border-indigo-500 ring-2 ring-indigo-500/30' : 'hover:border-indigo-400'
    )}
  >
    <button onClick={() => onSelect(source)} className="w-full text-left">
      <div className="w-full h-32 flex items-center justify-center bg-zinc-200">
        {source.thumbnail ? (
          <img src={source.thumbnail} alt={source.name} className="object-cover w-full h-32" />
        ) : (
          <span className="text-sm text-zinc-700">{source.name}</span>
        )}
      </div>
      <div className="p-2 text-sm truncate flex items-center gap-2">
        {source.appIcon && (
          <img
            src={source.appIcon}
            alt="AppIcon-src"
            className="w-4 h-4 object-contain rounded-sm"
          />
        )}
        <span className="text-sm truncate">{source.name}</span>
      </div>
    </button>
  </div>
));
